import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowRight, RotateCcw } from 'lucide-react'
import { Button } from '@/shared/components/ui/Button'
import { errorMessage } from '@/shared/i18n/errorMessage'
import { useTranslation } from '@/shared/i18n/useTranslation'
import { useRestartSurvivorGame } from '../queries'
import { SurvivorGameStatus, type SurvivorGameDetail } from '../types'
import styles from './SurvivorSeasonBanner.module.css'

/**
 * What happens next, once a game is over.
 *
 * Three states, and only one of them ever shows: the owner can start the next season; the next
 * season already exists and everyone gets a way through to it; or the competition has no rounds
 * left, which the server refuses on, so the button is not offered at all (`canRestart`).
 */
export function SurvivorSeasonBanner({ game }: { game: SurvivorGameDetail }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const restart = useRestartSurvivorGame(game.id)
  const [confirming, setConfirming] = useState(false)

  if (game.status !== SurvivorGameStatus.Finished) return null

  if (game.nextSeasonGameId) {
    const nextSeasonGameId = game.nextSeasonGameId
    return (
      <div className={styles.banner} role="status">
        <span className={styles.text}>
          {t('survivor.season.nextExists', { number: game.seasonNumber + 1 })}
        </span>
        <Button
          variant="secondary"
          onClick={() => void navigate({
            to: '/sobrevivente/$gameId',
            params: { gameId: nextSeasonGameId },
            search: { gw: undefined },
          })}
        >
          {t('survivor.season.goToNext')}
          <ArrowRight size={15} aria-hidden="true" />
        </Button>
      </div>
    )
  }

  if (!game.canRestart) return null

  function handleRestart() {
    if (!confirming) {
      setConfirming(true)
      return
    }
    restart.mutate(undefined, {
      // The new season is a different game, so staying put would leave the owner looking at the
      // archived one wondering whether anything happened.
      onSuccess: (result) => void navigate({
        to: '/sobrevivente/$gameId',
        params: { gameId: result.gameId },
        search: { gw: undefined },
      }),
    })
  }

  return (
    <div className={styles.banner}>
      <span className={styles.text}>
        {confirming ? t('survivor.season.restartConfirm') : t('survivor.season.restartPrompt')}
      </span>
      <Button variant="primary" onClick={handleRestart} disabled={restart.isPending}>
        <RotateCcw size={15} aria-hidden="true" />
        {confirming ? t('survivor.season.restartConfirmCta') : t('survivor.season.restart')}
      </Button>
      {restart.error && (
        <p className={styles.error} role="alert">
          {errorMessage(restart.error, t, 'error.survivor.restart')}
        </p>
      )}
    </div>
  )
}
